// frontend/src/features/ogdb2/pages/WellDetailPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { OGDB2 } from "../ogdb2Api";

import FactsTab from "../detail/tabs/FactsTab";
import ValuationTab from "../detail/tabs/ValuationTab";
import { OGDB2_METRICS, OGDB2_METRIC_CATEGORIES } from "../metricRegistry";

const TABS = [
  { key: "facts", label: "Facts" },
  { key: "valuation", label: "Valuation" },
  { key: "metrics", label: "Metrics" }, // ✅ NEW: show all registry metrics in one place
];

export default function WellDetailPage({ email, well, onBack }) {
  const [tab, setTab] = useState("facts");
  const [error, setError] = useState("");

  const [localWell, setLocalWell] = useState(well || null);

  useEffect(() => {
    setLocalWell(well || null);
    setTab("facts");
  }, [well?.id]);

  const taxYear = localWell?.tax_year;

  const [metricsPayload, setMetricsPayload] = useState(null);
  const resolved = useMemo(() => metricsPayload?.resolved || {}, [metricsPayload]);

  useEffect(() => {
    if (!localWell?.id) return;
    setError("");
    OGDB2.listWellMetrics(localWell.id, taxYear)
      .then((res) => setMetricsPayload(res.data))
      .catch((e) => setError(e?.response?.data?.detail || "Failed to load metrics"));
  }, [localWell?.id, taxYear]);

  async function upsertScenarioMetric(scenario, metric_code, value_num, value_text = null, unit = null) {
    if (!localWell?.id) return;
    setError("");
    try {
      await OGDB2.upsertMetric(localWell.id, {
        tax_year: taxYear,
        scenario, // assessor | actual | ba
        metric_code,
        value_num,
        value_text,
        unit,
        source: "manual",
        updated_by: email || null,
      });
      const res = await OGDB2.listWellMetrics(localWell.id, taxYear);
      setMetricsPayload(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to save value");
    }
  }

  async function patchWell(patch) {
    if (!localWell?.id) return;
    setError("");
    try {
      const res = await OGDB2.patchWell(localWell.id, patch);
      setLocalWell(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to update well");
    }
  }

  const registryByCategory = useMemo(() => {
    const cats = {
      production: [],
      expenses: [],
      price_deck: [],
      valuation: [],
      other: [],
    };

    for (const m of OGDB2_METRICS || []) {
      if (!m?.code) continue;
      const c = m.categories || [];

      // Metrics can belong to multiple categories; for display we assign a preferred one.
      if (c.includes(OGDB2_METRIC_CATEGORIES.PRODUCTION)) cats.production.push(m);
      else if (c.includes(OGDB2_METRIC_CATEGORIES.EXPENSES)) cats.expenses.push(m);
      else if (c.includes(OGDB2_METRIC_CATEGORIES.PRICE_DECK)) cats.price_deck.push(m);
      else if (c.includes(OGDB2_METRIC_CATEGORIES.VALUATION)) cats.valuation.push(m);
      else cats.other.push(m);
    }

    return cats;
  }, []);

  if (!localWell) return null;

  return (
    <div className="space-y-4">
      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {String(error)}
        </div>
      )}

      <div className="rounded-2xl border border-gray-200 bg-white p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm text-gray-500">Well</div>
            <div className="text-lg font-semibold text-gray-900">
              {localWell.well_name || "Untitled"}{" "}
              <span className="text-gray-400 font-normal">(RRC {localWell.rrc_number})</span>
            </div>
            <div className="text-xs text-gray-500">
              Tax Year {localWell.tax_year} · Operator: {localWell.operator || "—"} · County:{" "}
              {localWell.county || "—"}
            </div>
          </div>

          <button className="btn" onClick={onBack}>
            Back
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white">
        <div className="border-b border-gray-200 px-4 py-2 flex items-center gap-2">
          {TABS.map((t) => (
            <button
              key={t.key}
              className={tab === t.key ? "btn-primary" : "btn"}
              onClick={() => {
                // Force-save any active input by triggering blur before tab swap
                if (document.activeElement && typeof document.activeElement.blur === "function") {
                  document.activeElement.blur();
                }
                setTimeout(() => setTab(t.key), 0);
              }}
              type="button"
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-4">
          {tab === "facts" && <FactsTab well={localWell} resolved={resolved} onPatchWell={patchWell} />}

          {tab === "valuation" && (
            <ValuationTab
              well={localWell}
              resolved={resolved}
              metrics={metricsPayload?.metrics || []}
              onUpsertMetric={upsertScenarioMetric}
              onPatchWell={patchWell}
              email={email}
            />
          )}

          {tab === "metrics" && (
            <MetricsTab
              resolved={resolved}
              registryByCategory={registryByCategory}
              // NOTE: This is read-only for now. We can add inline edits later if you want.
            />
          )}
        </div>
      </div>
    </div>
  );
}

function MetricsTab({ resolved, registryByCategory }) {
  // Decide what you want visible here.
  // If you don’t want valuation drivers duplicated (since Valuation tab edits them), keep valuation collapsed or remove it.
  const sections = [
    { key: "production", title: "Production", items: registryByCategory.production },
    { key: "expenses", title: "Expenses", items: registryByCategory.expenses },
    { key: "price_deck", title: "Price Deck", items: registryByCategory.price_deck },
    { key: "valuation", title: "Valuation Drivers", items: registryByCategory.valuation },
    { key: "other", title: "Other", items: registryByCategory.other },
  ];

  return (
    <div className="space-y-4">
      <div className="text-sm font-semibold text-gray-800">Metrics</div>
      <div className="rounded-xl border border-gray-100 bg-gray-50 p-3 text-xs text-gray-600">
        This view is generated from the Metric Registry. It shows what’s stored for this well across scenarios.
      </div>

      {sections.map((s) => (
        <div key={s.key} className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
          <div className="px-4 py-3 border-b bg-gray-50">
            <div className="text-sm font-semibold text-gray-900">{s.title}</div>
            <div className="text-xs text-gray-500">{(s.items || []).length} metrics</div>
          </div>

          <div className="overflow-auto">
            <table className="min-w-[900px] w-full text-sm">
              <thead className="bg-white text-gray-600 text-left">
                <tr className="border-b">
                  <th className="py-2 px-3">Metric</th>
                  <th className="py-2 px-3">Assessor</th>
                  <th className="py-2 px-3">Actual</th>
                  <th className="py-2 px-3">BA</th>
                  <th className="py-2 px-3">Unit</th>
                </tr>
              </thead>
              <tbody>
                {(s.items || []).map((m) => {
                  const row = resolved?.[m.code] || {};
                  return (
                    <tr key={m.code} className="border-t">
                      <td className="py-2 px-3">
                        <div className="font-semibold text-gray-900">{m.label || m.code}</div>
                        <div className="text-xs text-gray-500 font-mono">{m.code}</div>
                      </td>
                      <td className="py-2 px-3">{fmtVal(row.assessor)}</td>
                      <td className="py-2 px-3">{fmtVal(row.actual)}</td>
                      <td className="py-2 px-3">{fmtVal(row.ba)}</td>
                      <td className="py-2 px-3">{row.unit || m.unit || "—"}</td>
                    </tr>
                  );
                })}

                {(s.items || []).length === 0 && (
                  <tr>
                    <td className="py-3 px-3 text-gray-500" colSpan={5}>
                      No metrics in this group.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="px-4 py-2 text-xs text-gray-500 border-t">
            Tip: if a value is blank here, it means no metric exists for that scenario/code yet.
          </div>
        </div>
      ))}
    </div>
  );
}

function fmtVal(x) {
  if (x === null || x === undefined || x === "") return "—";
  if (typeof x === "number") return x.toLocaleString();
  return String(x);
}
